import React from "react";
import { Link } from "react-router-dom";
import { CLBadge, TierTag } from "@/components/Badges";
import { SignalBadge } from "@/components/SignalBadge";
import { timeAgo } from "@/lib/format";
import { Lock, Share2 } from "lucide-react";
import { toast } from "sonner";

export function ArticleCard({ a, variant = "default" }) {
  const feature = variant === "feature";
  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/article/${a.slug}`;
    try {
      if (navigator.share) { await navigator.share({ title: a.title, text: a.summary || a.title, url }); return; }
    } catch { return; }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    catch { toast.error("Could not copy link"); }
  };
  return (
    <Link
      to={`/article/${a.slug}`}
      data-testid={`article-card-${a.slug}`}
      className="cl-card group block overflow-hidden"
    >
      {a.hero_image && (
        <div className={`relative overflow-hidden ${feature ? "h-64 sm:h-80" : "h-44"}`}>
          <img
            src={a.hero_image}
            alt={a.title}
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {(a.badges || []).slice(0, 2).map((b) => (
              <CLBadge key={b} label={b} />
            ))}
          </div>
          {a.signal_score != null && (
            <div className="absolute bottom-3 right-3">
              <SignalBadge score={a.signal_score} size="sm" />
            </div>
          )}
          <button data-testid={`share-card-${a.slug}`} onClick={share} title="Share"
            className="absolute top-3 right-3 grid place-items-center w-8 h-8 rounded-full bg-black/50 border border-white/15 text-white hover:text-[#ccff00] hover:border-[#ccff00]/50 backdrop-blur transition-colors">
            <Share2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2 text-[11px] font-mono-cl tracking-[0.14em] uppercase text-zinc-500">
          <span className="text-[#ccff00]">{a.category}</span>
          <span>·</span>
          <span>{timeAgo(a.published_at)}</span>
          {a.region && a.region !== "global" && (
            <>
              <span>·</span>
              <span className="uppercase">{a.region}</span>
            </>
          )}
          <TierTag tier={a.tier} />
        </div>
        <h3
          className={`font-display font-bold leading-tight group-hover:text-[#ccff00] transition-colors ${
            feature ? "text-2xl sm:text-3xl" : "text-lg"
          }`}
        >
          {a.title}
        </h3>
        {a.summary && (
          <p className={`text-zinc-400 mt-2 ${feature ? "text-base" : "text-sm"} line-clamp-3`}>
            {a.summary}
          </p>
        )}
        {a.analyst_name && (
          <p className="mt-2 font-mono-cl text-[10px] tracking-[0.12em] uppercase text-[#ccff00]">By {a.analyst_name}</p>
        )}
        {a.locked && (
          <div className="flex items-center gap-1.5 mt-3 text-[11px] font-mono-cl tracking-[0.14em] uppercase text-[#ccff00]">
            <Lock className="w-3 h-3" /> {a.blur_preview ? "Exclusive · Locked" : `${a.tier} access`}
          </div>
        )}
      </div>
    </Link>
  );
}
